import React, { useEffect, useState } from 'react'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import {
  selectRoleName,
  selectSubskills,
  selectSubskillsLoading,
  selectRoleSkills,
  selectRoleSkillsLoading,
  selectRoleSkillsError,
  fetchRoleSkills,
  setUserSkillName,
  updateUserRoleSkill
} from '../../redux/skillSlice'
import RoleSearchSkeleton from '../../components/RoleSearchSkeleton'
import { selectInitSessionUserDetails } from '../../redux/onboardingUserDetails'
import ProgressBar from '../../components/ProgressBar'
import { Mixpanel } from '../../config/Mixpanel'
import constants from '../../utilities/constants'
import { selectIsAnalysisPageVisited, selectIsPricingPageVisited } from '../../redux/newAnalysisSlice'
const RoleSkills = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Get data from Redux
  const subSkills = useSelector(selectSubskills)
  const subskillsLoading = useSelector(selectSubskillsLoading)
  const roleName = useSelector(selectRoleName) || 'Product Manager'

  // New role skills data from API
  const roleSkills = useSelector(selectRoleSkills)
  const roleSkillsLoading = useSelector(selectRoleSkillsLoading)
  const roleSkillsError = useSelector(selectRoleSkillsError)
  const userSessionDetails = useSelector(selectInitSessionUserDetails)

  const isAnalysisPageVisited = useSelector(selectIsAnalysisPageVisited)
  const isPricingPageVisited = useSelector(selectIsPricingPageVisited)

  // Sample skills data - replace with actual API data
  const [skillsData, setSkillsData] = useState([
    {
      id: 1,
      name: 'Metrics & Data',
      description: 'The foundation art of problem solving and value.',
      category: 'Core Skill',
      priority: 'high'
    },
    {
      id: 2,
      name: 'Product Strategy',
      description: 'Developing comprehensive product roadmaps and vision.',
      category: 'Recommended',
      priority: 'medium'
    },
    {
      id: 3,
      name: 'User Research',
      description: 'Understanding customer needs and market demands.',
      category: null,
      priority: 'medium'
    },
    {
      id: 4,
      name: 'Technical Skills',
      description: 'Working with engineering and technical specifications.',
      category: null,
      priority: 'low'
    },
    {
      id: 5,
      name: 'Leadership',
      description: 'Leading cross-functional teams and stakeholder management.',
      category: 'Fast-Track',
      priority: 'high'
    }
  ])

  // State for selected skill (single selection)
  const [selectedSkill, setSelectedSkill] = useState(null)


  useEffect(() => {
    if (isAnalysisPageVisited) {
      navigate('/onboarding/analysis')
    }

    else if (isPricingPageVisited) {
      navigate('/onboarding/pricing')
    }
  }, [dispatch, isAnalysisPageVisited, isPricingPageVisited, navigate])

  useEffect(() => {
    // Fetch role skills on component mount if we have a role name
    if (roleName && roleName.trim()) {
      try {
        dispatch(fetchRoleSkills({ role: roleName }))
      } catch (error) {
        console.error('Error dispatching fetchRoleSkills:', error)
      }
    }
  }, [dispatch, roleName])

  useEffect(() => {
    // Track page load
    try {
      Mixpanel.track(constants?.Mixpanel?.ROLE_SKILLS_PAGE_LOADED, {
        role_name: roleName || '',
        has_role_skills: roleSkills && roleSkills?.length > 0,
        has_sub_skills: subSkills && subSkills?.length > 0,
        session_id: userSessionDetails?.session_id || '',
        timestamp: new Date().toISOString()
      })
    } catch (error) {
      console.error('Error tracking page load:', error)
    }
  }, [roleName, roleSkills, subSkills, userSessionDetails?.session_id])

  useEffect(() => {
    // Use role skills from API if available, otherwise fall back to existing logic
    if (roleSkills && roleSkills?.length > 0) {
      try {
        const formattedSkills = roleSkills?.slice(0, 5)?.map((skill, index) => ({
          id: index + 1,
          name: skill?.name || `Skill ${index + 1}`,
          description: skill?.description || 'Skill description goes here.',
          category: skill?.type || null,
          priority: index < 2 ? 'high' : index < 4 ? 'medium' : 'low'
        }))
        setSkillsData(formattedSkills)
      } catch (error) {
        console.error('Error formatting role skills:', error)
        // Fallback to default skills if there's an error
      }
    } else if (subSkills && subSkills?.length > 0) {
      try {
        // Fallback to existing subSkills logic
        const formattedSkills = subSkills?.slice(0, 5)?.map((skill, index) => ({
          id: skill?.id || index + 1,
          name: skill?.name || `Skill ${index + 1}`,
          description: skill?.description || 'Skill description goes here.',
          category: index === 0 ? 'Core Skill' : index === 1 ? 'Recommended' : index === 4 ? 'Fast-Track' : null,
          priority: index < 2 ? 'high' : index < 4 ? 'medium' : 'low'
        }))
        setSkillsData(formattedSkills)
      } catch (error) {
        console.error('Error formatting subskills:', error)
        // Keep default skills data if there's an error
      }
    }
  }, [roleSkills, subSkills, roleName])

  const handleSkillSelect = (skillId) => {
    const wasSelected = selectedSkill === skillId
    const newSelectedSkill = wasSelected ? null : skillId

    // Single selection - if the same skill is clicked, deselect it; otherwise select the new skill
    setSelectedSkill(newSelectedSkill)

    // Track skill selection/deselection
    const selectedSkillData = skillsData?.find(skill => skill?.id === skillId)
    if (wasSelected) {
      Mixpanel?.track(constants?.Mixpanel?.ROLE_SKILLS_SKILL_DESELECTED, {
        skill_name: selectedSkillData?.name,
        skill_id: skillId,
        role_name: roleName,
        skill_category: selectedSkillData?.category,
        skill_priority: selectedSkillData?.priority,
        timestamp: new Date().toISOString()
      })
    } else {
      Mixpanel?.track(constants?.Mixpanel?.ROLE_SKILLS_SKILL_SELECTED, {
        skill_name: selectedSkillData?.name,
        skill_id: skillId,
        role_name: roleName,
        skill_category: selectedSkillData?.category,
        skill_priority: selectedSkillData?.priority,
        timestamp: new Date().toISOString()
      })
    }
  }

  const handleStartPersonalisation = () => {
    if (selectedSkill !== null) {
      const selectedSkillData = skillsData?.find(skill => skill?.id === selectedSkill)

      // Track start personalization clicked
      Mixpanel?.track(constants?.Mixpanel?.ROLE_SKILLS_START_PERSONALIZATION_CLICKED, {
        selected_skill_name: selectedSkillData?.name,
        selected_skill_id: selectedSkill,
        role_name: roleName,
        skill_category: selectedSkillData?.category,
        skill_priority: selectedSkillData?.priority,
        session_id: userSessionDetails?.session_id || '',
        timestamp: new Date().toISOString()
      })

      dispatch(setUserSkillName({ skillName: selectedSkillData?.name }))
      dispatch(updateUserRoleSkill({ session_id: userSessionDetails?.session_id, skill: selectedSkillData?.name }))
      navigate('/onboarding/1')
    }
  }

  const getCategoryStyle = (category) => {
    switch (category) {
      case 'Core Skill':
        return 'text-[rgba(255,255,255,0.5)]'
      case 'Recommended':
        return 'text-[rgba(255,255,255,0.5)]'
      case 'Fast-Track':
        return 'text-[rgba(255,255,255,0.5)]'
      case 'In-Demand':
        return 'text-[rgba(255,255,255,0.5)]'
      default:
        return 'text-transparent'
    }
  }

  // Show loading state while fetching role skills or if subskills are loading
  if (roleSkillsLoading || subskillsLoading) {
    return <RoleSearchSkeleton />
  }

  // Show error state if role skills failed to load
  if (roleSkillsError) {
    // Track API error
    Mixpanel?.track(constants?.Mixpanel?.ROLE_SKILLS_API_ERROR, {
      role_name: roleName,
      error_message: roleSkillsError,
      timestamp: new Date().toISOString()
    })

    return (
      <div className='mt-8 w-full md:max-w-[45rem] flex-1 mx-auto px-4 pb-4 lg:pb-6 lg:px-6 text-white relative flex flex-col justify-center items-center'>
        <div className="flex flex-col items-center justify-center gap-4">
          <div className='text-red-400 text-center'>
            <p className='font-bold text-[1rem]'>Failed to load skills</p>
            <p className='text-sm opacity-80'>{roleSkillsError}</p>
          </div>
          <button
            onClick={() => {
              // Track retry clicked
              Mixpanel?.track(constants?.Mixpanel?.ROLE_SKILLS_RETRY_CLICKED, {
                role_name: roleName,
                error_message: roleSkillsError,
                timestamp: new Date().toISOString()
              })
              dispatch(fetchRoleSkills({ role: roleName }))
            }}
            className='px-4 py-2 bg-[#406AFF] hover:bg-[#5078FF] rounded-lg text-white font-medium transition-colors'
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className='mt-8 w-full md:max-w-[45rem] flex-1 mx-auto px-4 pb-4 lg:pb-6 lg:px-6 text-white relative flex flex-col justify-between items-center'>
      {/* Main content container */}

      <div className='flex flex-col justify-start items-center w-full'>

        <ProgressBar
          currentStep={2}
          totalSteps={8}
          stepText={
            `Step 2/8 of becoming ${roleName} `
          }
        />
        <div className='mt-4 w-full flex flex-col p-[2px] rounded-[1.5rem]' style={{
          backgroundImage: `linear-gradient(
            to right,
            rgba(64, 106, 255, 0.3) 0%,
            rgba(64, 106, 255, 1) 56%,
            rgba(64, 106, 255, 0.3) 100%
          )`
        }}>
          <div className='w-full flex flex-col rounded-[1.5rem] bg-[#0c2346] shadow-[0px_0px_10px_0px_rgba(0,0,0,0.5)] relative overflow-hidden p-6'>

            {/* Background pattern */}
            <div className="absolute inset-0 opacity-40 rounded-3xl bg-repeat bg-[length:180px_180px] pointer-events-none"
              style={{ backgroundImage: `url('https://img.learntube.ai/public-assets/personalise/web.webp')` }} />

            {/* Background overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-[rgba(64,106,255,0.15)] to-transparent rounded-[1.5rem]" />

            {/* Content */}
            <div className='relative z-10 flex flex-col gap-8'>
              {/* Header */}
              <div className='flex flex-col'>
                <h1 className='bg-gradient-to-r from-white to-[#FFE994] bg-clip-text text-transparent font-bold text-[1.75rem] leading-[1.2]'>
                  What would you like to start with for {roleName}?
                </h1>
              </div>

              {/* Skills list */}
              <div className='flex flex-col gap-2'>
                {skillsData?.map((skill) => (
                  <div
                    key={skill?.id}
                    onClick={() => handleSkillSelect(skill?.id)}
                    className={`
                      bg-gradient-to-b from-[#142d84] to-[#060f2f] 
                      border-[rgba(64,106,255,0.2)] border border-solid 
                      rounded-xl p-3 cursor-pointer transition-all duration-200
                      hover:from-[#1a3a9e] hover:to-[#0a1540] hover:border-[rgba(64,106,255,0.4)]
                      ${selectedSkill === skill?.id ? 'ring-2 ring-[#7FC241] ring-opacity-50' : ''}
                    `}
                  >
                    <div className='flex items-start justify-between w-full'>
                      <div className='flex items-center gap-3 flex-1 min-w-0'>
                        {/* Skill indicator dot */}
                        <div className='relative flex-shrink-0 w-[14px] h-[14px]'>
                          <div className={`
                            absolute inset-[-7.14%] w-4 h-4 rounded-full
                            ${selectedSkill === skill?.id
                              ? ''
                              : ''
                            }
                          `} />
                          <div className={`
                            absolute inset-0 w-[14px] h-[14px] rounded-full border-2
                            ${selectedSkill === skill?.id
                              ? 'border-[#7FC241]'
                              : 'border-[#7FC241]'
                            }
                          `} />
                          {selectedSkill === skill?.id && (
                            <div className='absolute inset-0 flex items-center justify-center'>
                              <div className='w-1.5 h-1.5 bg-[#7FC241] rounded-full' />
                            </div>
                          )}
                        </div>

                        {/* Skill content */}
                        <div className='flex flex-col gap-2.5 flex-1 min-w-0'>
                          <div className='font-semibold text-[#70aeff] text-[14px] leading-[1.4] truncate'>
                            {skill?.name}
                          </div>
                          <div className='text-[rgba(131,190,255,0.4)] text-[10px] leading-[1.3] font-normal'>
                            {skill?.description}
                          </div>
                        </div>
                      </div>

                      {/* Category badge */}
                      {skill?.category && (
                        <div className={`flex-shrink-0 font-normal italic text-[9px] leading-none h-[20px] flex items-center justify-center ${getCategoryStyle(skill?.category)}`}>
                          {skill?.category}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Start button */}
              <button
                onClick={handleStartPersonalisation}
                disabled={selectedSkill === null}
                className={`
                  flex items-center justify-center gap-2 w-full h-12 rounded-lg
                  font-semibold text-[#121212] text-lg transition-all duration-200
                  ${selectedSkill === null
                    ? 'bg-[#7FC241]/50 cursor-not-allowed border-[#8bd845]/50'
                    : 'bg-[#7FC241] hover:bg-[#8bd845] border-b-2 border-l-2 border-r-2 border-[#8bd845] hover:scale-[0.98]'
                  }
                `}
              >
                <span>Start Personalising Now</span>
                <div className='flex items-center justify-center w-3 h-[7px]'>
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className='text-[#121212] text-xs'
                  />
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Company logos and text */}
        <div className='mt-5 mb-3 flex flex-col gap-3 items-center justify-center'>
          <p className='bg-gradient-to-r from-white to-[#1156a3] bg-clip-text text-transparent font-medium italic text-[12px] leading-[1.4] text-center'>
            Commonly asked in interviews at leading companies
          </p>
          <div className='w-full flex items-center justify-center'>
            <img src="https://img.learntube.ai/public-assets/personalise/Frame%20427323026.webp" alt="Company logo" className='w-[80%] h-full object-cover' />
          </div>
        </div>
      </div>
    </div>
  )
}

export default RoleSkills